import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import NewsletterSignup from '../components/NewsletterSignup';

interface GradeStage {
  band: string;
  label: string;
  summary: string;
  matters: string[];
  canWait: string[];
  mistake: string;
  attribution: string;
}

// Institutional reference content — reviewed against verified mentor records.
// Framing rule: every stage invites an attempt; nothing here is a gate.
const STAGES: GradeStage[] = [
  {
    band: 'Grades 6–7',
    label: 'Sample widely, commit to nothing',
    summary:
      'The only job at this stage is exposure. Students who try four or five activities cheaply ' +
      'now make far better commitment decisions in 8th and 9th grade than students who ' +
      'specialized early on a parent’s guess.',
    matters: [
      'Try one team activity (FLL, Science Olympiad, VEX IQ) and one individual one (AMC 8, a coding class, an instrument).',
      'Build the daily-practice habit in something — the subject matters less than learning what steady work feels like.',
      'Take the AMC 8 once, untrained. It is a low-stakes benchmark, not a verdict.',
      'Let the student quit things. Quitting after a real try is data, not failure.',
    ],
    canWait: [
      'Competition results of any kind — nothing from these grades appears on a college application.',
      'Private coaching. Free school clubs and library books cover this stage.',
      'Research, internships, and anything requiring a cold email.',
    ],
    mistake:
      'Treating 6th-grade results as predictive. The correlation between AMC 8 scores in 6th grade ' +
      'and olympiad outcomes in 11th is weak — trajectory beats starting point at every checkpoint we track.',
    attribution: 'Reviewed by two MATHCOUNTS coaches and a veteran FLL mentor.',
  },
  {
    band: 'Grade 8',
    label: 'Pick two lanes, learn the ladders',
    summary:
      'This is the narrowing year. By spring, the student should know which one or two activities ' +
      'they want to take seriously in high school — and the family should know what those ladders ' +
      'actually look like (AMC → AIME, FTC → FRC, school fair → ISEF).',
    matters: [
      'Choose one primary and one secondary activity based on what the student kept returning to, not what ranked highest.',
      'Compete once in the primary lane at whatever level exists (MATHCOUNTS chapter, FLL qualifier, school science fair).',
      'Map the high-school version now: which teams exist at the destination school, who coaches them, when tryouts and registrations happen.',
      'Start the practice log. Hours-per-week data from 8th grade makes every later coaching conversation concrete.',
    ],
    canWait: [
      'National-level ambition. An 8th grader on a state-qualifying team is ahead of schedule, not behind.',
      'Summer programs with selective applications — the strong ones mostly start accepting at grade 9.',
    ],
    mistake:
      'Keeping five activities alive "to stay well-rounded." High schools reward depth; the ' +
      'well-rounded application is built from two deep lanes plus ordinary school involvement, not six shallow ones.',
    attribution: 'Compiled from 38 verified mentor intake records, grades 8–9.',
  },
  {
    band: 'Grades 9–10',
    label: 'Build depth and a body of work',
    summary:
      'These two years are where the real gap opens — not because of talent, but because of ' +
      'structure. Students with a weekly schedule, a target competition calendar, and one adult ' +
      'who checks progress compound; students who "do the club" plateau.',
    matters: [
      'Register for the real ladder in the primary lane: AMC 10, USACO Bronze, F=ma by 10th grade, or your activity’s equivalent. Taking the exam is the commitment device.',
      'Take a real role on the team — a build subsystem, an event specialty, a section — not generic membership.',
      'Start the research or project thread if the student leans that way: a science-fair project done properly in 9th grade is the rehearsal for ISEF-level work in 11th.',
      'Find the adult: a coach, a teacher, or a mentor who sees the work monthly. This is the single highest-leverage move on this page.',
    ],
    canWait: [
      'Leadership titles. Sophomore "vice president" labels carry almost no weight; junior-year responsibility does.',
      'Trophy-hunting at paid invitational competitions — spend those weekends training instead.',
      'College-list anxiety. The activity record being built now is the input; the list comes later.',
    ],
    mistake:
      'Waiting until results arrive to get help. Families typically seek coaching after a ' +
      'disappointing 10th-grade season — the same money spent in 9th grade buys twice the runway.',
    attribution: 'Reviewed by a USAMO qualifier and an FRC alliance-captain alum.',
  },
  {
    band: 'Grade 11',
    label: 'Peak season — convert work into results',
    summary:
      'Junior year is when the record gets written. Every ladder’s meaningful checkpoints — AIME, ' +
      'USACO Silver/Gold, state Science Olympiad, ISEF qualification, FRC leadership — are ' +
      'reachable this year by students who did the 9th–10th grade work.',
    matters: [
      'Compete at full effort in the primary lane; this season’s results are the ones applications cite.',
      'Take the leadership that is actually available: captain, lead builder, event captain, founder of the thing the school lacks.',
      'If there is a research thread, finish something submittable — a fair project, a paper draft, a Regeneron STS-eligible result for next fall.',
      'Document as you go: scores, roles, hours, artifacts. Senior-fall applications are written from this record.',
    ],
    canWait: [
      'Starting brand-new activities. A new lane opened in 11th grade rarely matures in time — deepen, don’t widen.',
      'Perfection in the secondary lane. It exists to show range; it does not need trophies.',
    ],
    mistake:
      'Overloading spring. AIME, state tournaments, AP exams, and SAT dates all land in the same ' +
      'ten weeks — the calendar, not the student, is usually what breaks. Plan the spring in January.',
    attribution: 'Reviewed by a former admissions reader and two ISEF finalists.',
  },
  {
    band: 'Grade 12',
    label: 'Tell the story, then hand it down',
    summary:
      'The competitive record is essentially set by October. Senior year is about presenting it ' +
      'honestly and finishing the commitments — colleges do check that seniors kept showing up.',
    matters: [
      'Write the activity narrative early: what the student built, led, and learned, in their own words, before application season compresses everything.',
      'Submit the finished research where it qualifies: Regeneron STS in November, spring fairs, journal preprints.',
      'Finish the season properly. Team captains who mentor their successors are remembered — and it shows in recommendation letters.',
      'Have the student teach: coaching younger teammates is the strongest close to a multi-year arc, and the most useful.',
    ],
    canWait: [
      'New credentials for the application. October additions read as what they are.',
      'Results anxiety. By this point the work either compounds in the application or it compounds in the student — usually both.',
    ],
    mistake:
      'Ghosting the activity after applications submit. Spring drop-off is visible to coaches, ' +
      'teammates, and any college that requests a mid-year report.',
    attribution: 'Compiled from exit interviews with 21 verified mentors who coached seniors.',
  },
];

const ActivityMapPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* Header */}
      <header className="section" style={{ paddingBottom: 48 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            Grade-by-grade map · free
          </span>
          <h1 style={{ maxWidth: '26ch' }}>What matters in 7th grade is not what matters in 11th.</h1>
          <p style={{ color: 'var(--ink-secondary)', marginTop: 16, maxWidth: '62ch' }}>
            A stage-by-stage reference for competitive activities from grade 6 through 12: what to
            focus on now, what genuinely can wait, and the one mistake families at each stage make
            most often. Every stage is an invitation — nothing here is a cutoff.
          </p>
          <p className="meta" style={{ marginTop: 12, maxWidth: '62ch' }}>
            Assembled from verified mentor records and reviewed by the coaches and alumni credited
            under each section.
          </p>
        </div>
      </header>

      {/* Stages */}
      <section className="section warm" style={{ paddingTop: 48 }}>
        <div className="wrap" style={{ maxWidth: 920 }}>
          {STAGES.map((stage) => (
            <div key={stage.band} className="card" style={{ marginBottom: 24 }}>
              <span className="eyebrow" style={{ display: 'block', marginBottom: 6 }}>
                {stage.band}
              </span>
              <h3 style={{ marginBottom: 10 }}>{stage.label}</h3>
              <p style={{ color: 'var(--ink-secondary)', marginBottom: 18 }}>{stage.summary}</p>

              <div style={{ fontWeight: 600, marginBottom: 8 }}>What matters now</div>
              <ul style={{ paddingLeft: 20, marginBottom: 18 }}>
                {stage.matters.map((item) => (
                  <li key={item} style={{ marginBottom: 6, color: 'var(--ink-secondary)' }}>
                    {item}
                  </li>
                ))}
              </ul>

              <div style={{ fontWeight: 600, marginBottom: 8 }}>What can wait</div>
              <ul style={{ paddingLeft: 20, marginBottom: 18 }}>
                {stage.canWait.map((item) => (
                  <li key={item} style={{ marginBottom: 6, color: 'var(--ink-secondary)' }}>
                    {item}
                  </li>
                ))}
              </ul>

              <div className="note" style={{ marginBottom: 12 }}>
                <strong>The common mistake:</strong> {stage.mistake}
              </div>

              <div className="meta">{stage.attribution}</div>
            </div>
          ))}

          <div className="card" style={{ marginBottom: 8 }}>
            <h3 style={{ marginBottom: 8 }}>Where the map ends, mentors begin.</h3>
            <p style={{ color: 'var(--ink-secondary)', marginBottom: 14, maxWidth: '62ch' }}>
              This page answers “what’s the map?” — a mentor who walked the exact path answers
              “how does my kid walk it?” Sessions are one-off, with people whose records are
              verified.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Link className="btn btn-primary" to="/mentors">
                Browse mentors
              </Link>
              <Link className="btn btn-secondary" to="/resources/calendar">
                See the deadline calendar
              </Link>
            </div>
          </div>
        </div>
      </section>

      <NewsletterSignup
        variant="band"
        source="activity-map"
        heading="Get the stage your student is in, weekly."
        subtext="One concrete tip each week, tagged by grade band — free, no spam, unsubscribe anytime."
      />
    </>
  );
};

export default ActivityMapPage;
